<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  fetchUserRelations,
  fetchUsers,
  getMediaUrl,
} from "@/services/backofficeClient";

const DEFAULT_PAGE_SIZE = 25;
const ANY_TYPED_FILTER = "any_typed";
const TYPE_OPTIONS = [
  "like",
  "super_like",
  "dislike",
  "block",
  "direct_message",
  "messaged",
];
const TYPE_COLORS = {
  like: "#2fb344",
  super_like: "#0ca678",
  dislike: "#f76707",
  block: "#d63939",
  direct_message: "#17a2b8",
  messaged: "#6c757d",
};

const filters = reactive({
  fromUser: "",
  toUser: "",
  userIds: "",
  type: "",
});
const relations = ref([]);
const isLoading = ref(true);
const errorMessage = ref("");
const currentPage = ref(1);
const pagination = ref({
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalItems: 0,
  totalPages: 1,
  hasNext: false,
});
const route = useRoute();
const router = useRouter();

const normalizedPageSize = computed(() => {
  const size = Number(pagination.value.pageSize);
  return Number.isFinite(size) && size > 0 ? size : DEFAULT_PAGE_SIZE;
});
const totalItems = computed(() => {
  const total = Number(pagination.value.totalItems);
  return Number.isFinite(total) && total >= 0 ? total : 0;
});
const totalPages = computed(() => {
  const pages = Number(pagination.value.totalPages);
  return Number.isFinite(pages) && pages > 0 ? pages : 1;
});
const rangeStart = computed(() => {
  if (totalItems.value === 0) return 0;
  return (Math.max(1, currentPage.value) - 1) * normalizedPageSize.value + 1;
});
const rangeEnd = computed(() => {
  if (totalItems.value === 0) return 0;
  return Math.min(currentPage.value * normalizedPageSize.value, totalItems.value);
});
const canGoPrev = computed(() => currentPage.value > 1 && !isLoading.value);
const canGoNext = computed(
  () => currentPage.value < totalPages.value && !isLoading.value
);
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && relations.value.length === 0
);

const fromUserSuggestions = ref([]);
const toUserSuggestions = ref([]);
const fromUserLoading = ref(false);
const toUserLoading = ref(false);
let fromUserSearchTimeout = null;
let toUserSearchTimeout = null;

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatType = (value) => {
  if (!value) return "--";
  return String(value).replace(/_/g, " ");
};

const typeBadgeStyle = (type) => {
  const color = TYPE_COLORS[type] || "#6c757d";
  return {
    background: color,
    color: "#fff",
  };
};

const normalizeUserIdsInput = () => {
  if (!filters.userIds) return [];
  return filters.userIds
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean);
};

const loadUserSuggestions = async (searchTerm, target) => {
  const trimmed = typeof searchTerm === "string" ? searchTerm.trim() : "";
  if (!trimmed || trimmed.length < 2) {
    if (target === "from") fromUserSuggestions.value = [];
    if (target === "to") toUserSuggestions.value = [];
    return;
  }

  const setLoading = (value) => {
    if (target === "from") fromUserLoading.value = value;
    if (target === "to") toUserLoading.value = value;
  };
  const setSuggestions = (list) => {
    if (target === "from") fromUserSuggestions.value = list;
    if (target === "to") toUserSuggestions.value = list;
  };

  setLoading(true);
  try {
    const payload = await fetchUsers({
      search: trimmed,
      pageSize: 5,
      page: 1,
    });
    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const parsed = Array.isArray(rawItems)
      ? rawItems
        .filter((item) => item && typeof item === "object")
        .map((item) => parseUser(item))
        .filter(Boolean)
      : [];
    setSuggestions(parsed);
  } catch (error) {
    console.error("Failed to load user suggestions", error);
    setSuggestions([]);
  } finally {
    setLoading(false);
  }
};

const debounceSearch = (value, target) => {
  const timeoutRef = target === "from" ? "fromUserSearchTimeout" : "toUserSearchTimeout";
  if (timeoutRef === "fromUserSearchTimeout" && fromUserSearchTimeout) {
    clearTimeout(fromUserSearchTimeout);
  }
  if (timeoutRef === "toUserSearchTimeout" && toUserSearchTimeout) {
    clearTimeout(toUserSearchTimeout);
  }
  const handler = setTimeout(() => loadUserSuggestions(value, target), 250);
  if (timeoutRef === "fromUserSearchTimeout") {
    fromUserSearchTimeout = handler;
  } else {
    toUserSearchTimeout = handler;
  }
};

const selectSuggestedUser = (userId, target) => {
  if (target === "from") {
    filters.fromUser = userId;
    fromUserSuggestions.value = [];
  } else {
    filters.toUser = userId;
    toUserSuggestions.value = [];
  }
};

const getFirstPhotoPath = (user) => {
  if (!user || typeof user !== "object") return null;
  const atoms = Array.isArray(user.atoms) ? user.atoms : [];
  const photoAtom = atoms.find(
    (atom) => atom && atom.attribute === "photo" && atom.mediaPath
  );
  return photoAtom?.mediaPath || null;
};

const parseUser = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id ? String(raw.id) : "";
  const name =
    raw.name ||
    raw.displayName ||
    raw.fullName ||
    [raw.firstName, raw.lastName]
      .filter(Boolean)
      .map((v) => String(v))
      .join(" ")
      .trim() ||
    id;
  const firstPhotoPath = raw.firstPhotoPath || getFirstPhotoPath(raw);
  const photoUrl = firstPhotoPath ? getMediaUrl(firstPhotoPath) : null;
  return {
    id,
    name,
    photoUrl,
    label: name && id ? `${name} (${id})` : id || name,
  };
};

const parseRelation = (item) => {
  const atomRef =
    item && typeof item === "object" && item.atomReference
      ? item.atomReference
      : item.atom_reference;
  return {
    fromUserId: item.fromUserId || item.from_user_id || "",
    toUserId: item.toUserId || item.to_user_id || "",
    fromUser: parseUser(item.fromUser || item.from_user),
    toUser: parseUser(item.toUser || item.to_user),
    type: item.type || "",
    previousType: item.previousType || item.previous_type || "",
    isSeen: Boolean(item.isSeen ?? item.is_seen),
    placement: item.placement || "",
    atomReference: atomRef
      ? {
        id: atomRef.id || "",
        attribute: atomRef.attribute || "",
        userId: atomRef.userId || atomRef.user_id || "",
      }
      : null,
    createdAt: parseDate(item.createdAt || item.created_at),
    updatedAt: parseDate(item.updatedAt || item.updated_at),
  };
};

const resolvePagination = (rawPagination, itemsLength) => {
  const fallbackTotalPages = Math.max(
    1,
    Math.ceil((itemsLength || 0) / DEFAULT_PAGE_SIZE)
  );
  if (rawPagination && typeof rawPagination === "object") {
    const page = Number(rawPagination.page) || currentPage.value;
    const pageSize = Number(rawPagination.pageSize) || DEFAULT_PAGE_SIZE;
    const totalItemsValue =
      Number(rawPagination.totalItems) || Number(itemsLength) || 0;
    const totalPagesValue =
      Number(rawPagination.totalPages) || fallbackTotalPages;
    return {
      page: page > 0 ? page : 1,
      pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
      totalItems: totalItemsValue >= 0 ? totalItemsValue : 0,
      totalPages: totalPagesValue > 0 ? totalPagesValue : fallbackTotalPages,
      hasNext:
        rawPagination.hasNext === true
          ? true
          : (page || 1) < (totalPagesValue || fallbackTotalPages),
    };
  }
  return {
    page: currentPage.value,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: itemsLength,
    totalPages: fallbackTotalPages,
    hasNext: fallbackTotalPages > currentPage.value,
  };
};

const loadRelations = async () => {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    const payload = await fetchUserRelations({
      fromUser: filters.fromUser?.trim() || undefined,
      toUser: filters.toUser?.trim() || undefined,
      userIds: normalizeUserIdsInput(),
      type: filters.type || undefined,
      page: currentPage.value,
      pageSize: DEFAULT_PAGE_SIZE,
    });

    const rawItems = Array.isArray(payload) ? payload : payload?.items;
    const parsed = Array.isArray(rawItems)
      ? rawItems
        .filter((item) => item && typeof item === "object")
        .map((item) => parseRelation(item))
      : [];

    const meta = resolvePagination(payload?.pagination, parsed.length);
    pagination.value = meta;
    currentPage.value = Math.min(
      Math.max(1, meta.page || 1),
      Math.max(1, meta.totalPages || 1)
    );
    relations.value = parsed;
  } catch (error) {
    console.error("Failed to fetch user relations", error);
    errorMessage.value =
      error?.payload?.message ||
      error?.message ||
      "Failed to load relations. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  const query = {};
  const from = filters.fromUser?.trim();
  const to = filters.toUser?.trim();
  const ids = normalizeUserIdsInput();
  const type = filters.type?.trim();
  if (from) query.fromUserId = from;
  if (to) query.toUserId = to;
  if (ids.length) query.userIds = ids.join(",");
  if (type) query.type = type;
  router.replace({ name: "UserRelations", query });
  loadRelations();
};

const resetFilters = () => {
  filters.fromUser = "";
  filters.toUser = "";
  filters.userIds = "";
  filters.type = "";
  currentPage.value = 1;
  router.replace({ name: "UserRelations" });
  loadRelations();
};

const changePage = (delta) => {
  const nextPage = currentPage.value + delta;
  if (nextPage < 1 || nextPage === currentPage.value) return;
  currentPage.value = nextPage;
  loadRelations();
};

const setFiltersFromRoute = () => {
  const query = route.query || {};
  filters.fromUser =
    typeof query.fromUserId === "string"
      ? query.fromUserId
      : typeof query.fromUser === "string"
        ? query.fromUser
        : "";
  filters.toUser =
    typeof query.toUserId === "string"
      ? query.toUserId
      : typeof query.toUser === "string"
        ? query.toUser
        : "";
  const userIdsFromQuery = [];
  if (Array.isArray(query.userIds)) {
    userIdsFromQuery.push(...query.userIds);
  } else if (typeof query.userIds === "string") {
    userIdsFromQuery.push(...query.userIds.split(/[\s,]+/));
  }
  filters.userIds = userIdsFromQuery.filter(Boolean).join(", ");
  filters.type = typeof query.type === "string" ? query.type : "";
};

watch(
  () => filters.fromUser,
  (value) => debounceSearch(value, "from")
);

watch(
  () => filters.toUser,
  (value) => debounceSearch(value, "to")
);

watch(
  () => route.query,
  () => {
    setFiltersFromRoute();
    currentPage.value = 1;
    loadRelations();
  }
);

onMounted(() => {
  setFiltersFromRoute();
  loadRelations();
});
</script>

<template>
  <div class="py-4 container-fluid">

    <div class="card">
      <div class="card-header pb-0 border-0">
        <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
          <div>
            <h6 class="mb-1">User Relations</h6>
            <p class="text-xs text-secondary mb-0">
              Page {{ currentPage }} of {{ totalPages }} ·
              {{ totalItems }} total · Showing {{ rangeStart }}-{{ rangeEnd }}
            </p>
          </div>
          <div class="mt-3 mt-lg-0 d-flex align-items-center gap-2 flex-wrap">
            <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="isLoading"
              @click="resetFilters">
              Reset filters
            </button>
            <button class="btn btn-sm btn-outline-success mb-0" type="button" :disabled="isLoading"
              @click="applyFilters">
              Apply filters
            </button>
            <button class="btn btn-sm btn-primary mb-0" type="button" :disabled="isLoading" @click="loadRelations">
              Refresh
            </button>
          </div>
        </div>
      </div>
      <div class="card-body pt-0">
        <div class="row g-3 mb-4">
          <div class="col-12 col-md-6 col-lg-3 position-relative">
            <label class="form-label text-xs text-uppercase text-secondary fw-bold mb-1">
              From user ID
            </label>
            <input v-model="filters.fromUser" class="form-control" type="text" placeholder="User ID of sender" />
            <div v-if="fromUserLoading" class="text-xs text-secondary mt-1">
              Searching users...
            </div>
            <div v-else-if="fromUserSuggestions.length" class="autocomplete-dropdown shadow-sm">
              <button v-for="user in fromUserSuggestions" :key="user.id" type="button"
                class="list-group-item list-group-item-action py-1 px-2" @click="selectSuggestedUser(user.id, 'from')">
                {{ user.label || user.id }}
              </button>
            </div>
          </div>
          <div class="col-12 col-md-6 col-lg-3 position-relative">
            <label class="form-label text-xs text-uppercase text-secondary fw-bold mb-1">
              To user ID
            </label>
            <input v-model="filters.toUser" class="form-control" type="text" placeholder="User ID of recipient" />
            <div v-if="toUserLoading" class="text-xs text-secondary mt-1">
              Searching users...
            </div>
            <div v-else-if="toUserSuggestions.length" class="autocomplete-dropdown shadow-sm">
              <button v-for="user in toUserSuggestions" :key="user.id" type="button"
                class="list-group-item list-group-item-action py-1 px-2" @click="selectSuggestedUser(user.id, 'to')">
                {{ user.label || user.id }}
              </button>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <label class="form-label text-xs text-uppercase text-secondary fw-bold mb-1">
              User IDs (comma or space separated)
            </label>
            <textarea v-model="filters.userIds" class="form-control" rows="1"
              placeholder="e.g. 64f..., 64a..., 64b..."></textarea>
          </div>
          <div class="col-12 col-lg-2">
            <label class="form-label text-xs text-uppercase text-secondary fw-bold mb-1">
              Type
            </label>
            <select v-model="filters.type" class="form-select">
              <option value="">Any</option>
              <option :value="ANY_TYPED_FILTER">Any (excluding none)</option>
              <option v-for="type in TYPE_OPTIONS" :key="type" :value="type">
                {{ formatType(type) }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger" role="alert">
          {{ errorMessage }}
        </div>

        <div class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  From
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  To
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Type
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Previous
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Seen
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Placement
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Atom
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Created
                </th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="isLoading">
                <td colspan="9" class="text-center py-4">
                  Loading relations...
                </td>
              </tr>
              <tr v-else-if="isEmpty">
                <td colspan="9" class="text-center py-4">
                  No relations found for the selected filters.
                </td>
              </tr>
              <tr v-else v-for="relation in relations"
                :key="`${relation.fromUserId}-${relation.toUserId}-${relation.createdAt}`">
                <td>
                  <div class="d-flex align-items-center">
                    <div class="me-2">
                      <img v-if="relation.fromUser?.photoUrl" :src="relation.fromUser.photoUrl" alt="from user photo"
                        class="avatar avatar-sm border-radius-md" />
                      <div v-else
                        class="avatar avatar-sm bg-gradient-secondary shadow-sm d-flex align-items-center justify-content-center text-white text-xxs">
                        <i class="ni ni-circle-08"></i>
                      </div>
                    </div>
                    <div class="d-flex flex-column">
                      <router-link v-if="relation.fromUserId" :to="`/users/${relation.fromUserId}`"
                        class="text-info text-sm text-start text-decoration-none">
                        {{ relation.fromUser?.name || relation.fromUserId }}
                      </router-link>
                      <span v-else class="text-info text-sm text-start">--</span>
                      <small class="text-muted">{{ relation.fromUserId || "--" }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <div class="me-2">
                      <img v-if="relation.toUser?.photoUrl" :src="relation.toUser.photoUrl" alt="to user photo"
                        class="avatar avatar-sm border-radius-md" />
                      <div v-else
                        class="avatar avatar-sm bg-gradient-secondary shadow-sm d-flex align-items-center justify-content-center text-white text-xxs">
                        <i class="ni ni-circle-08"></i>
                      </div>
                    </div>
                    <div class="d-flex flex-column">
                      <router-link v-if="relation.toUserId" :to="`/users/${relation.toUserId}`"
                        class="text-info text-sm text-start text-decoration-none">
                        {{ relation.toUser?.name || relation.toUserId }}
                      </router-link>
                      <span v-else class="text-info text-sm text-start">--</span>
                      <small class="text-muted">{{ relation.toUserId || "--" }}</small>
                    </div>
                  </div>
                </td>
                <td class="text-sm">
                  <span class="badge text-uppercase" :style="typeBadgeStyle(relation.type)">
                    {{ formatType(relation.type) }}
                  </span>
                </td>
                <td class="text-sm">
                  {{ formatType(relation.previousType) }}
                </td>
                <td class="text-sm">
                  <span :class="['badge', relation.isSeen ? 'bg-gradient-success' : 'bg-gradient-secondary']">
                    {{ relation.isSeen ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td class="text-sm">
                  {{ relation.placement || '--' }}
                </td>
                <td class="text-sm">
                  <div v-if="relation.atomReference" class="d-flex flex-column">
                    <span>{{ relation.atomReference.attribute || 'Atom' }} · {{ relation.atomReference.id }}</span>
                    <small class="text-muted">user: {{ relation.atomReference.userId }}</small>
                  </div>
                  <span v-else>--</span>
                </td>
                <td class="text-sm">
                  {{ formatDate(relation.createdAt) }}
                </td>
                <td class="text-sm">
                  {{ formatDate(relation.updatedAt) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex flex-column flex-md-row align-items-center justify-content-between border-top pt-3 mt-3">
          <p class="text-sm text-secondary mb-2 mb-md-0">
            Showing {{ rangeStart }}-{{ rangeEnd }} of {{ totalItems }}
          </p>
          <div class="d-flex align-items-center gap-2">
            <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="!canGoPrev"
              @click="changePage(-1)">
              Previous
            </button>
            <span class="text-sm text-secondary">
              Page {{ currentPage }} of {{ totalPages }}
            </span>
            <button class="btn btn-sm btn-outline-secondary mb-0" type="button" :disabled="!canGoNext"
              @click="changePage(1)">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.autocomplete-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  max-height: 220px;
  overflow-y: auto;
}
</style>
